import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import {
  SearchIcon,
  ServerIcon,
  BookOpenIcon,
  DatabaseIcon,
  PlugIcon,
  ZapIcon,
} from "lucide-react";
import { useVisibleNavItems } from "@/hooks/useVisibleNavItems";
import { flattenNavItems, kbSqlSubNav } from "@/lib/navigationItems";
import { PageHeader } from "@/components/ui/page-header";
import {
  ActionCard,
  ActionCardGrid,
  ActionCardSection,
} from "@/components/ui/action-card";

export const Route = createFileRoute("/portal/knowledgebase-sql/")({
  component: KnowledgebaseSqlDashboard,
});

interface NavCard {
  title: string;
  description: string;
  icon: typeof SearchIcon;
  href: string;
  iconColor: string;
}

const navCards: NavCard[] = [
  {
    title: "Ask Database",
    description:
      "Query your databases using natural language. Get instant answers without writing SQL.",
    icon: SearchIcon,
    href: "/portal/knowledgebase-sql/query",
    iconColor: "bg-purple-500",
  },
  {
    title: "Quick Question",
    description:
      "Ask a question and an AI analyst explores your database step by step, then answers in plain English with the SQL and results.",
    icon: ZapIcon,
    href: "/portal/knowledgebase-sql/quick-question",
    iconColor: "bg-cyan-600",
  },
  {
    title: "Data Sources",
    description:
      "Manage database connections and configure which data sources are available for querying.",
    icon: ServerIcon,
    href: "/portal/knowledgebase-sql/data-sources",
    iconColor: "bg-blue-500",
  },
  {
    title: "Business Glossary",
    description:
      "Define business terms and mappings to help the AI understand your domain language.",
    icon: BookOpenIcon,
    href: "/portal/knowledgebase-sql/glossary",
    iconColor: "bg-emerald-500",
  },
  {
    title: "Connect Claude",
    description:
      "Query these data sources from your own Claude — claude.ai, Claude Desktop or Claude Code.",
    icon: PlugIcon,
    href: "/portal/knowledgebase-sql/connect",
    iconColor: "bg-amber-500",
  },
];

function KnowledgebaseSqlDashboard() {
  // A card shows exactly when its sub-nav tab does — the sql_rag /
  // claude_connect split (claude-connect A1) and every other gate are the
  // sub-nav's, never restated here.
  const visibleSubNav = useVisibleNavItems(kbSqlSubNav);
  const hrefs = new Set(flattenNavItems(visibleSubNav).map((i) => i.href));
  const visibleNavCards = navCards.filter((card) => hrefs.has(card.href));
  return (
    <>
      <Helmet>
        <title>Knowledgebase Database | Portal</title>
      </Helmet>

      <div className="space-y-6">
        <PageHeader
          title="Knowledgebase Database"
          description="Query your databases using natural language powered by AI."
          icon={DatabaseIcon}
          eyebrow="AI Knowledge"
          gradient
        />

        <ActionCardSection title="Features">
          <ActionCardGrid columns={3}>
            {visibleNavCards.map((card) => (
              <ActionCard
                key={card.href}
                title={card.title}
                description={card.description}
                icon={card.icon}
                href={card.href}
                iconColor={card.iconColor}
              />
            ))}
          </ActionCardGrid>
        </ActionCardSection>
      </div>
    </>
  );
}
